import { useCallback, useMemo, useState } from 'react';

import {
  EmployeeIdentificationType,
  EmployeeViewModel,
  EmploymentViewModel,
  EmploymentViewModelNew,
  UpdateEmploymentInput,
} from '@fora-monorepo/employer-my-pages-common/types';
import { trackCustomEvent } from '@fora-monorepo/webapp-analytics';
import {
  Animate,
  Assets,
  Button,
  ButtonTextOrIcon,
  Container,
  Feedback,
  Form,
  formatIDNumberOnChange,
  OptionType,
  useNumericInputField,
  useNumericInputFieldAsync,
  useRadioButton,
  useRadioButtonWithValidate,
  useSelectSingle,
  useTextInputField,
} from '@foradevelopment/fora-ui';

import {
  validateCity,
  validateCoAddress,
  validateFirstName,
  validateLastName,
  validatePostalCodeInternational,
  validatePostalCodeSweden,
  validateStreetAddress,
  validateStreetAddress2,
} from '../../common/utils';
import {
  AddressForm,
  EmploymentFormStepWrapper,
  HAS_PARTIAL_DUPLICATE_MATCHES,
  PersonalIdentityRadioButtons,
  PersonalInformationForm,
  PersonDetails,
} from '../../components';
import { getCountriesOptions, getIDValidator } from '../utils';
import {
  getExactDuplicateEmployments,
  getPartialDuplicateEmployments,
  updateEmployment,
} from '../../redux/employment/employmentThunks';
import { useAppDispatch, useAppSelector } from '../../redux/hooks';
import { useTypedTranslation } from '../../i18n/i18n';
import { envConfig } from '../../config';
import { PartialDuplicateFormTwo } from '../../components/ManageEmployment/PartialDuplicateFormTwo';
import { links } from '../../router/links';

export const EditEmployeeForm = ({
  employment,
  goToNextPanel,
}: {
  employment: EmploymentViewModelNew;
  goToNextPanel: (employment: EmploymentViewModelNew) => void;
}) => {
  const { t } = useTypedTranslation();
  const dispatch = useAppDispatch();

  const countries = useAppSelector(state => state.foraGlobal.countries);

  const countriesOptions: OptionType[] = useMemo(() => getCountriesOptions(countries), [countries]);

  const [errorSubmittingMessage, setErrorSubmittingMessage] = useState('');
  const [submittingForm, setSubmittingForm] = useState(false);
  const [showNoChangesMadeMessage, setShowNoChangesMadeMessage] = useState(false);

  // PartialDuplicateForm
  const [partialDuplicateOptions, setPartialDuplicateOptions] = useState<EmploymentViewModelNew[]>([]);
  const [exactDuplicateOption, setExactDuplicateOption] = useState<EmploymentViewModel>();
  const partialDuplicateRadioInput = useRadioButtonWithValidate(''); // value is employeeId of duplicate
  const [, setSelectedDuplicateEmployment] = useState<{
    employee: EmployeeViewModel;
    isInactive: boolean;
    policyNumber: string;
  } | null>(null);

  const changeEmployeeIdRadioInput = useRadioButton('');

  const duplicateValidator = useCallback(
    async (value: string) => {
      const result = await dispatch(getExactDuplicateEmployments({ employeeId: value }));

      if (getExactDuplicateEmployments.fulfilled.match(result)) {
        const exactDuplicateFoundResult = result.payload;
        if (exactDuplicateFoundResult) {
          setExactDuplicateOption(exactDuplicateFoundResult);
        }
        // If no exact match was found, search with only birth date
        else if (changeEmployeeIdRadioInput.value === EmployeeIdentificationType.CoOrdinationNumber) {
          const result = await dispatch(
            getPartialDuplicateEmployments({ employeeId: value, includeNonVerifiedCoordinationNumbers: true })
          );
          if (getPartialDuplicateEmployments.fulfilled.match(result)) {
            const partialDuplicatesFoundResult = result.payload as EmploymentViewModelNew[];

            setPartialDuplicateOptions(partialDuplicatesFoundResult);
            if (partialDuplicatesFoundResult.length > 0) {
              return [HAS_PARTIAL_DUPLICATE_MATCHES];
            }
          }
        }
      }
      setPartialDuplicateOptions([]);
      return [];
    },
    [dispatch, changeEmployeeIdRadioInput.value]
  );

  // Personal details form (if unverified coordination number)
  const idValidator = useMemo(
    () => getIDValidator(changeEmployeeIdRadioInput.value || employment.employee.identificationType),
    [changeEmployeeIdRadioInput.value, employment.employee.identificationType]
  );
  const employeeIdInput = useNumericInputFieldAsync('', idValidator, duplicateValidator, formatIDNumberOnChange);

  const firstName = useTextInputField(employment.employee.firstName, validateFirstName);
  const lastName = useTextInputField(employment.employee.lastName, validateLastName);

  // AddressForm (if unverified coordination number)
  const initAddress = employment.employee.address;
  const countryInput = useSelectSingle(initAddress?.countryCode ?? 'SE');
  const coAddressInput = useTextInputField(initAddress?.coAddress ?? '', validateCoAddress);
  const streetAddressInput = useTextInputField(initAddress?.streetAddress ?? '', validateStreetAddress);
  const streetAddress2Input = useTextInputField(initAddress?.streetAddress2 ?? '', validateStreetAddress2);
  const postalCodeInputSwe = useNumericInputField(initAddress?.postalCode ?? '', validatePostalCodeSweden);
  const postalCodeInputIntl = useTextInputField(initAddress?.postalCode ?? '', validatePostalCodeInternational);
  const cityInput = useTextInputField(initAddress?.city ?? '', validateCity);

  const showNoChangesMadeNotification = async () => {
    setShowNoChangesMadeMessage(true);
    new Promise(resolve => setTimeout(resolve, 4000)).then(() => setShowNoChangesMadeMessage(false));
  };

  const isUnverifiedCoordinationNumber =
    employment.employee.identificationType === EmployeeIdentificationType.CoOrdinationNumber &&
    !employment.employee.isVerified;

  const submitEditEmployee = async () => {
    setSubmittingForm(true);
    const errors: string[] = [];
    let changesMade = false;
    let formIsValid = true;

    // Validate employee details
    if (isUnverifiedCoordinationNumber) {
      errors.push(...firstName.validateInputField(), ...lastName.validateInputField());
      changesMade =
        changesMade || [firstName.hasChangedSinceInit(), lastName.hasChangedSinceInit()].some(changeMade => changeMade);

      if (changeEmployeeIdRadioInput.value) {
        errors.push(...(await employeeIdInput.validateInputField()));
        changesMade = true;
      }

      if (changeEmployeeIdRadioInput.value !== EmployeeIdentificationType.PersonalIdentityNumber) {
        errors.push(
          ...coAddressInput.validateInputField(),
          ...streetAddressInput.validateInputField(),
          ...streetAddress2Input.validateInputField(),
          ...cityInput.validateInputField()
        );
        changesMade =
          changesMade ||
          [
            coAddressInput.hasChangedSinceInit(),
            streetAddressInput.hasChangedSinceInit(),
            streetAddress2Input.hasChangedSinceInit(),
            cityInput.hasChangedSinceInit(),
          ].some(changeMade => changeMade);
        if (countryInput.props.value === 'SE') {
          errors.push(...postalCodeInputSwe.validateInputField());
          changesMade = changesMade || postalCodeInputSwe.hasChangedSinceInit();
        } else {
          errors.push(...postalCodeInputIntl.validateInputField());
          changesMade = changesMade || postalCodeInputIntl.hasChangedSinceInit();
        }
      }
    }

    // Don't continue if no changes have been made
    if (!changesMade) {
      showNoChangesMadeNotification();
      setSubmittingForm(false);
      return;
    }
    formIsValid = errors.length === 0;

    if (formIsValid) {
      const updateEmploymentInput: UpdateEmploymentInput = {
        employeeId: employeeIdInput.props.value || employment.employee.employeeId,
        partnerNumberIndividual: employment.employee.partnerNumberIndividual,
        firstName: firstName.props.value || employment.employee.firstName,
        lastName: lastName.props.value || employment.employee.lastName,
        addressInfo:
          isUnverifiedCoordinationNumber &&
          changeEmployeeIdRadioInput.value !== EmployeeIdentificationType.PersonalIdentityNumber
            ? {
                countryCode: countryInput.props.value || employment.employee.address.countryCode,
                coAddress: coAddressInput.hasChangedSinceInit()
                  ? coAddressInput.props.value
                  : employment.employee.address.coAddress,
                streetAddress: streetAddressInput.props.value || employment.employee.address.streetAddress,
                streetAddress2: streetAddress2Input.hasChangedSinceInit()
                  ? streetAddress2Input.props.value
                  : employment.employee.address.streetAddress2,
                postalCode:
                  countryInput.props.value === 'SE'
                    ? postalCodeInputSwe.props.value
                    : postalCodeInputIntl.props.value || employment.employee.address.postalCode,
                city: cityInput.props.value || employment.employee.address.city,
              }
            : undefined,
      };

      setErrorSubmittingMessage('');

      const result = await dispatch(updateEmployment({ unchangedEmployment: employment, updateEmploymentInput }));
      if (updateEmployment.fulfilled.match(result)) {
        trackCustomEvent('Edited employment');
        goToNextPanel(result.payload);
      }
      if (updateEmployment.rejected.match(result)) {
        setErrorSubmittingMessage(
          envConfig.devLogging && result.payload && result.payload.message
            ? `Fel i backend: ${result.payload.message}`
            : t('errorMessages.contactSupport')
        );
        setSubmittingForm(false);
      }
    } else {
      setSubmittingForm(false);
    }
  };

  const deceasedAndUnverified = employment.employee.deathDate && !employment.employee.isVerified;
  return (
    <Form preventDefault>
      <Container marginBottom={[40, 60]}>
        <PersonDetails
          firstName={employment.employee.firstName}
          lastName={employment.employee.lastName}
          identificationType={employment.employee.identificationType}
          employeeId={employment.employee.employeeId}
        />
      </Container>

      {isUnverifiedCoordinationNumber && (
        <>
          <EmploymentFormStepWrapper heading={t('form.editEmployee.personalInformationTitle')} isFirstStep={true}>
            <PersonalIdentityRadioButtons
              radioButtonProps={changeEmployeeIdRadioInput}
              name={firstName.props.value || employment.employee.firstName}
            />
            <PersonalInformationForm
              identificationType={(changeEmployeeIdRadioInput.value as EmployeeIdentificationType) || ''}
              employeeIdInput={employeeIdInput.props}
              firstNameInput={firstName.props}
              lastNameInput={lastName.props}
            />
            {(exactDuplicateOption || partialDuplicateOptions.length > 0) && (
              <PartialDuplicateFormTwo
                partialDuplicateOptions={partialDuplicateOptions}
                partialDuplicateRadioInput={partialDuplicateRadioInput}
                setSelectedDuplicateEmployment={setSelectedDuplicateEmployment}
                employmentsLink={links.employments}
                exactDuplicateOption={exactDuplicateOption}
                employeeIdInputValue={employeeIdInput.props.value}
                inputIdType={changeEmployeeIdRadioInput.value as EmployeeIdentificationType}
              />
            )}
          </EmploymentFormStepWrapper>

          {changeEmployeeIdRadioInput.value !== EmployeeIdentificationType.PersonalIdentityNumber && (
            <EmploymentFormStepWrapper heading={t('form.editEmployee.addressInformationTitle')}>
              <AddressForm
                countryInput={countryInput.props}
                coStreetAddressInput={coAddressInput.props}
                streetAddressInput={streetAddressInput.props}
                streetAddress2Input={streetAddress2Input.props}
                postalCodeInput={
                  countryInput.props.value === 'SE' ? postalCodeInputSwe.props : postalCodeInputIntl.props
                }
                cityInput={cityInput.props}
                isForeign={countryInput.props.value !== 'SE'}
                countriesOptions={countriesOptions}
              />
            </EmploymentFormStepWrapper>
          )}
        </>
      )}

      {errorSubmittingMessage && (
        <Feedback type="error" marginBottom={40} negativeSideMargins>
          {errorSubmittingMessage}
        </Feedback>
      )}
      {deceasedAndUnverified || !employment.employee.deathDate ? (
        <Button type="button" onClick={submitEditEmployee} disabled={submittingForm} fullWidth={false}>
          <ButtonTextOrIcon
            icon={<Assets.Spinner center={false} width={20} color="white" />}
            textColor="white"
            isLoading={submittingForm}
          >
            {t('common.save')}
          </ButtonTextOrIcon>
        </Button>
      ) : null}
      <Animate.Height isTriggered={showNoChangesMadeMessage}>
        {showNoChangesMadeMessage && (
          <Feedback type="warning" size="slim" marginTop={20}>
            {t('form.editEmployee.noChangesMade')}
          </Feedback>
        )}
      </Animate.Height>
    </Form>
  );
};
